import { useCountryCodeToLicensePlate } from "@tm/context-distribution"
import { SuggestionField, SuggestionFieldButtonGroup } from "@tm/controls"
import { useLocalization } from "@tm/localization"
import { VehicleSearchType, VehicleShortInfo } from "@tm/models"
import { concat, plateCountryCodeMapper } from "@tm/utils"
import { useCallback, useRef, useState, RefObject } from "react"

import { Alert, Stack, styled } from "@tm/components"
import { Vehicles } from "@tm/data"
import { renderVehicleShortInfoAutosuggestColumns } from "./VehicleShortInfoAutosuggest"

const SearchWrapper = styled("div", {
    name: "LicensePlateSearchWrapper",
    slot: "root",
    overridesResolver: (_, styles) => styles.root,
    shouldForwardProp: (prop) => prop !== "hasError",
})<{
    hasError?: boolean
    size: "xs" | "s" | "m" | "l" | "xl"
}>(({ hasError, theme, size = "m" }) => {
    const fieldHeight = { xs: 32, s: 32, m: 38, l: 48, xl: 48 } // there a no xs or xl sizes, but i keep them because changing could be break something else
    const iconSize = { xs: 16, s: 16, m: 18, l: 24, xl: 24 }
    const paddings = { xs: "0 6px", s: "0 6px", m: "0 9px", l: "0 10px", xl: "0 10px" }

    return {
        border: hasError ? `1px solid ${theme.colors?.warning ?? theme.palette.warning ?? "yellow"}` : `1px solid ${theme.palette.grey[300]}`,
        height: fieldHeight[size],
        "&:active,&:focus,&:focus-within": {
            borderColor: theme.palette.primary.main,
            boxShadow: theme.shadows[4],
        },
        "& .inputgroup:not(.pseudogroup)>.inputgroup__children:first-child": {
            border: 0,
        },
        "& .inputgroup:not(.pseudogroup)>.inputgroup__children:last-child": {
            padding: paddings[size],
            borderWidth: 1,
            borderRadius: 0,
        },
        "& .btn .icon": {
            width: iconSize[size],
            height: iconSize[size],
        },
        "&.plate-id--s .plate-id__fx": {
            fontSize: 9,
            backgroundSize: "60%",
        },
        "&.plate-id--m .plate-id__fx": {
            fontSize: 11,
            backgroundSize: "60%",
        },
        "&.plate-id--l .plate-id__fx": {
            fontSize: 13,
            backgroundSize: "60%",
        },
    }
})

type Props = {
    initialValue?: string
    onSearch(query: string): Promise<void>
    onAttachVehicleId?(vehicleId: string, query: string): void

    size: "xs" | "s" | "m" | "l" | "xl"
    className?: string
    shortCountryCode: string
    showCountryCode?: boolean
    placeholder?: string
    searchType?: VehicleSearchType
    onlyNumbersAndDigits?: boolean
    hidePlateFx?: boolean
    inputFieldRef?: RefObject<HTMLInputElement>
}

export default function VehicleSearchField(props: Props) {
    const localization = useLocalization()
    const { translateText } = localization

    const [query, setQuery] = useState(props.initialValue || "")
    const [suggestions, setSuggestions] = useState<{ loading: boolean; vehicles: Array<VehicleShortInfo> }>({ loading: false, vehicles: [] })
    const searchFieldRef = useRef<HTMLInputElement>(null)
    const { plateCode } = useCountryCodeToLicensePlate()
    const [loading, setLoading] = useState<boolean>(false)
    const [errorMessage, setErrorMessage] = useState<string>("")

    const handleRequestSuggestions = useCallback(
        (queryString: string) => {
            if (!queryString) {
                setSuggestions({ loading: false, vehicles: [] })
                return
            }

            setSuggestions((state) => ({
                loading: true,
                vehicles: state.vehicles,
            }))

            Vehicles.Gsi.findVehicles({
                query: queryString,
                searchType: props.searchType ?? VehicleSearchType.PlateIdOrVin,
                pageSize: 10,
            })
                .then((response) =>
                    setSuggestions({
                        loading: false,
                        vehicles: response?.vehicles ?? [],
                    })
                )
                .catch(() => {
                    setSuggestions({
                        loading: false,
                        vehicles: [],
                    })
                })
        },
        [props.searchType]
    )

    const changeQuery = (queryString: string) => {
        let newQuery = queryString
        if (newQuery && props.onlyNumbersAndDigits) {
            newQuery = queryString.replace(/[^A-Za-z0-9 -äöüÄÖÜ]/g, "")
        }

        setErrorMessage("")
        setQuery(newQuery)
    }

    const resetQuery = () => {
        setQuery("")
        setErrorMessage("")
        ;(searchFieldRef as unknown as RefObject<SuggestionField<unknown>>).current?.focus(false) // can be removed as soon as we use vehiclesearch v2 only
    }

    const handleVehicleSuggestionSelect = (vehicleSuggestion: VehicleShortInfo) => {
        if (props.onAttachVehicleId && vehicleSuggestion.id) {
            setLoading(true)
            setSuggestions({
                loading: false,
                vehicles: [],
            })
            props.onAttachVehicleId(vehicleSuggestion.id, query)
        }
    }

    const handleSearch = () => {
        if (!query) {
            ;(searchFieldRef as unknown as RefObject<SuggestionField<unknown>>).current?.focus(false) // can be removed as soon as we use vehiclesearch v2 only
        } else {
            setErrorMessage("")
            setLoading(true)
            props
                .onSearch(query)
                .catch(() => {
                    setErrorMessage(translateText(1266))
                    ;(searchFieldRef as unknown as RefObject<SuggestionField<unknown>>).current?.focus(false) // can be removed as soon as we use vehiclesearch v2 only
                })
                .finally(() => {
                    setLoading(false)
                })
        }
    }

    const { size } = props
    const mappedCountryCode = plateCountryCodeMapper(props.shortCountryCode)

    return (
        <Stack direction="column">
            <SearchWrapper
                hasError={!!errorMessage}
                size={size}
                className={concat(
                    " ",
                    "plate-id",
                    props.className,
                    `plate-id--${mappedCountryCode.toLocaleLowerCase()}`,
                    `plate-id--${size}`,
                    suggestions.vehicles.length ? "has-suggestions" : ""
                )}
            >
                {!props.hidePlateFx && <div className="plate-id__fx">{props.showCountryCode && mappedCountryCode?.toUpperCase()}</div>}
                <LicensePlateSearchGroup
                    value={query}
                    onChange={changeQuery}
                    onChangeConfirm={handleSearch}
                    onSuggestionSelect={suggestions.vehicles.length > 0 ? handleVehicleSuggestionSelect : undefined}
                    onChangeReset={resetQuery}
                    suggestions={suggestions.vehicles}
                    requestSuggestions={handleRequestSuggestions}
                    renderTableColumns={
                        suggestions.vehicles.length > 0
                            ? () => renderVehicleShortInfoAutosuggestColumns(suggestions.vehicles, localization, plateCode)
                            : undefined
                    }
                    showClearTooltip={translateText(48)}
                    maxHeight={500}
                    maxLength={17}
                    size={size}
                    placeholder={props.placeholder}
                    ref={(props.inputFieldRef ?? searchFieldRef) as RefObject<unknown> as RefObject<SuggestionFieldButtonGroup<unknown>>}
                    handleSearchButtonClick={handleSearch}
                    suggestDelay={500}
                    loading={loading || suggestions.loading}
                    minCharactersToSuggest={3}
                />
            </SearchWrapper>
            {!!errorMessage && <Alert size="extraSmall" severity="warning" title={errorMessage} variant="filled" />}
        </Stack>
    )
}

const LicensePlateSearchGroup = styled(
    SuggestionFieldButtonGroup,
    {}
)((theme) => {
    return {
        border: 0,
        padding: 0,
        borderTopRightRadius: 0,
        borderBottomRightRadius: 0,
    }
})
